import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import PlayingCard from "./PlayingCard";
import { X, ArrowUp, ArrowDown, Coins, GripHorizontal } from "lucide-react";

/**
 * Draggable trade window for outgoing pending trades
 * Design: Dark theme with subtle accent colors
 */
export default function OutgoingTradeWindow({
  trade,
  position, // "top-left" | "right" | "bottom-right"
  stackIndex = 0,
  onCancel,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const windowRef = useRef(null);

  // Calculate initial position based on target player position
  useEffect(() => {
    const baseOffset = stackIndex * 15; // Stack offset
    let initialPos = { x: 0, y: 0 };
    
    switch (position) {
      case "top-left":
        initialPos = { x: 20 + baseOffset, y: 120 + baseOffset };
        break;
      case "right":
        initialPos = { x: window.innerWidth - 280 - baseOffset, y: 200 + baseOffset };
        break;
      case "bottom-right":
        initialPos = { x: window.innerWidth - 280 - baseOffset, y: window.innerHeight - 250 - baseOffset };
        break;
      default:
        initialPos = { x: 100 + baseOffset, y: 100 + baseOffset };
    }
    
    setCurrentPos(initialPos);
  }, [position, stackIndex]);

  // Mouse/Touch handlers for dragging
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setDragOffset({
      x: clientX - currentPos.x,
      y: clientY - currentPos.y
    });
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setCurrentPos({
      x: clientX - dragOffset.x,
      y: clientY - dragOffset.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset]);

  return (
    <div
      ref={windowRef}
      className="fixed z-30 w-52 bg-card/95 backdrop-blur-sm border border-amber-900/30 rounded-lg shadow-lg"
      style={{
        left: currentPos.x,
        top: currentPos.y,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {/* Drag Handle */}
      <div
        className="flex items-center justify-between px-2 py-1.5 bg-amber-900/20 rounded-t-lg cursor-grab active:cursor-grabbing border-b border-amber-900/20"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="flex items-center gap-1.5">
          <GripHorizontal className="w-3 h-3 text-amber-600/50" />
          <span className="text-xs font-medium text-amber-200/90">→ {trade.to_nickname}</span>
        </div>
        <span className="text-[9px] px-1.5 py-0.5 bg-amber-900/30 rounded text-amber-400/80">送信中</span>
      </div>

      {/* Content */}
      <div className="p-2 space-y-1.5">
        {/* Offered Cards */}
        {trade.offer_cards_data?.length > 0 && (
          <div className="flex items-start gap-1">
            <ArrowUp className="w-3 h-3 text-red-400/70 mt-1 flex-shrink-0" />
            <div className="flex flex-wrap gap-0.5">
              {trade.offer_cards_data.map((card, i) => (
                <PlayingCard key={i} suit={card.suit} rank={card.rank} isPublic={true} size="xxs" />
              ))}
            </div>
          </div>
        )}
        
        {/* Offered Chips */}
        {trade.offer_chips > 0 && (
          <div className="flex items-center gap-1 text-xs text-red-300/80">
            <ArrowUp className="w-3 h-3" />
            <Coins className="w-3 h-3 text-amber-500/70" />
            <span>{trade.offer_chips}</span>
          </div>
        )}

        {/* Requested Cards */}
        {trade.request_cards_data?.length > 0 && (
          <div className="flex items-start gap-1">
            <ArrowDown className="w-3 h-3 text-emerald-400/70 mt-1 flex-shrink-0" />
            <div className="flex flex-wrap gap-0.5">
              {trade.request_cards_data.map((card, i) => (
                <PlayingCard key={i} suit={card.suit} rank={card.rank} isPublic={true} size="xxs" />
              ))}
            </div>
          </div>
        )}
        
        {/* Requested Chips */}
        {trade.request_chips > 0 && (
          <div className="flex items-center gap-1 text-xs text-emerald-300/80">
            <ArrowDown className="w-3 h-3" />
            <Coins className="w-3 h-3 text-amber-500/70" />
            <span>{trade.request_chips}</span>
          </div>
        )}
      </div>

      {/* Cancel Button */}
      <div className="px-2 pb-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCancel(trade.id)}
          className="w-full h-6 text-[10px] text-red-400/80 hover:text-red-300 hover:bg-red-900/20 border border-red-900/30"
        >
          <X className="w-3 h-3 mr-1" />
          取り下げ
        </Button>
      </div>
    </div>
  );
}
